/**
 * Governance Controls Page — M6.3 Phase 1
 * Browse and filter the governance control library with domain and severity breakdowns
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import QMSLayout from '../components/QMSLayout';
import {
  Shield, Database, Lock, Server, Bell, Search, Filter,
  Loader2, AlertTriangle, ChevronDown, ChevronUp, Eye,
  ShieldCheck, ShieldAlert, Activity, FileText, Layers
} from 'lucide-react';

const DOMAIN_CONFIG = {
  data_integrity: { icon: Database, color: 'text-cyan-400', bg: 'bg-cyan-500/15', border: 'border-cyan-500/30' },
  cybersecurity: { icon: Lock, color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30' },
  governance: { icon: ShieldCheck, color: 'text-blue-400', bg: 'bg-blue-500/15', border: 'border-blue-500/30' },
  resilience: { icon: Server, color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30' },
  incident_notification: { icon: Bell, color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30' },
};

const DOMAIN_LABELS = {
  data_integrity: 'Data Integrity',
  cybersecurity: 'Cybersecurity',
  governance: 'Governance Controls',
  resilience: 'Resilience / Recovery',
  incident_notification: 'Incident / Notification',
};

const SEVERITY_CONFIG = {
  critical: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'Critical' },
  high: { bg: 'bg-orange-500/15', text: 'text-orange-400', label: 'High' },
  medium: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', label: 'Medium' },
  low: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Low' },
};

const CONTROL_TYPE_LABELS = {
  preventive: 'Preventive',
  detective: 'Detective',
  corrective: 'Corrective',
  directive: 'Directive',
};

const SeverityBadge = ({ severity }) => {
  const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.medium;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`} data-testid={`severity-badge-${severity}`}>
      {cfg.label}
    </span>
  );
};

const ControlCard = ({ control }) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation(['governance', 'common']);
  const domainCfg = DOMAIN_CONFIG[control.domain] || DOMAIN_CONFIG.governance;

  return (
    <div className="qms-card" data-testid={`control-card-${control.control_id}`}>
      <div className="qms-card-body p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${domainCfg.bg} ${domainCfg.color}`}>
              {control.control_code}
            </span>
            <h4 className="text-white text-sm font-medium truncate">{control.title}</h4>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <SeverityBadge severity={control.severity_if_missing} />
            <span className="text-[#8B95A5] text-xs bg-[rgba(255,255,255,0.05)] px-1.5 py-0.5 rounded">
              {CONTROL_TYPE_LABELS[control.control_type] || control.control_type}
            </span>
          </div>
        </div>

        <p className="text-[#8B95A5] text-xs leading-relaxed mb-2">{control.description}</p>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-[#36A9E1] text-xs hover:text-[#38BDF8] transition-colors"
          data-testid={`control-expand-${control.control_id}`}
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? t('common:actions.view') : t('common:labels.details')}
        </button>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.05)] space-y-2">
            <div>
              <span className="text-[#8B95A5] text-xs font-medium">Evidence Examples:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {control.evidence_examples.map((ex, i) => (
                  <span key={i} className="text-xs bg-[rgba(255,255,255,0.05)] text-[#B8C7D9] px-2 py-0.5 rounded">
                    {ex}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[#8B95A5] text-xs font-medium">Applicable Regions:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {control.applicable_regions.map((r, i) => (
                  <span key={i} className="text-xs bg-[rgba(54,169,225,0.1)] text-[#36A9E1] px-1.5 py-0.5 rounded font-mono">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const GovernanceControlsPage = () => {
  const { t, i18n } = useTranslation(['governance', 'common']);
  const { isAdmin, isAuditor } = useAuth();

  const [controls, setControls] = useState([]);
  const [stats, setStats] = useState(null);
  const [posture, setPosture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [ctlRes, statsRes, postureRes] = await Promise.all([
        fetch(`${API}/governance/frameworks/controls`, { credentials: 'include' }),
        fetch(`${API}/governance/frameworks/controls/stats`, { credentials: 'include' }),
        fetch(`${API}/governance/frameworks/posture`, { credentials: 'include' }),
      ]);
      if (ctlRes.status === 403) {
        setError(t('governance:controls.pageDescription'));
        return;
      }
      if (!ctlRes.ok || !statsRes.ok || !postureRes.ok) throw new Error(t('governance:controls.noControls'));
      const [ctlData, statsData, postureData] = await Promise.all([
        ctlRes.json(), statsRes.json(), postureRes.json(),
      ]);
      setControls(ctlData);
      setStats(statsData);
      setPosture(postureData);
    } catch (err) {
      console.error('Error loading governance controls:', err);
      setError(t('governance:controls.noControls'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    toast.loading(t('governance:pdfExport.exportingPdf'), { id: 'pdf-export' });
    try {
      const locale = i18n.language || 'en';
      const res = await fetch(`${API}/governance/posture/export/pdf?locale=${locale}`, { credentials: 'include' });
      if (!res.ok) { const d = await res.json().catch(() => ({})); toast.error(d.detail || t('governance:pdfExport.exportFailed'), { id: 'pdf-export' }); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CHARSTAN_Posture_Overview_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('governance:pdfExport.downloadStarted'), { id: 'pdf-export' });
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error(t('governance:pdfExport.exportFailed'), { id: 'pdf-export' });
    } finally {
      setExportingPdf(false);
    }
  };

  const filteredControls = controls.filter(c => {
    if (domainFilter !== 'all' && c.domain !== domainFilter) return false;
    if (severityFilter !== 'all' && c.severity_if_missing !== severityFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q) || c.control_code.toLowerCase().includes(q);
    }
    return true;
  });

  if (!isAdmin && !isAuditor) {
    return (
      <QMSLayout title={t('governance:controls.title')}>
        <div className="qms-card">
          <div className="qms-card-body p-8 text-center">
            <Shield className="mx-auto mb-4 text-red-400" size={48} />
            <h3 className="text-white font-semibold mb-2">{t('common:labels.accessRestricted')}</h3>
            <p className="text-[#8B95A5] text-sm">{t('governance:controls.pageDescription')}</p>
          </div>
        </div>
      </QMSLayout>
    );
  }

  if (loading) {
    return (
      <QMSLayout title={t('governance:controls.title')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </QMSLayout>
    );
  }

  const bandColor = posture?.overall_band === 'Strong' ? 'text-emerald-400'
    : posture?.overall_band === 'Controlled with gaps' ? 'text-blue-400'
    : posture?.overall_band === 'Weak / Improvement required' ? 'text-yellow-400'
    : 'text-red-400';

  return (
    <QMSLayout title={t('governance:controls.title')}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Layers className="text-[#36A9E1]" size={28} />
            <h1 className="font-['Syne'] text-2xl font-bold text-white" data-testid="governance-controls-title">{t('governance:controls.pageTitle')}</h1>
          </div>
          {(isAdmin || isAuditor) && (
            <button
              onClick={handleExportPdf}
              disabled={exportingPdf || loading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#C9A84C]/20 text-[#C9A84C] text-sm font-medium hover:bg-[#C9A84C]/30 disabled:opacity-50 transition-colors border border-[#C9A84C]/30"
              data-testid="export-posture-pdf-btn"
            >
              {exportingPdf ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {exportingPdf ? t('governance:pdfExport.exportingPdf') : t('governance:pdfExport.exportPdf')}
            </button>
          )}
        </div>
        <p className="text-[#8B95A5] text-sm">
          {t('governance:controls.pageDescription')}
        </p>
      </div>

      {/* Posture Score Banner */}
      {posture && (
        <div className="qms-card mb-6" data-testid="posture-score-banner">
          <div className="qms-card-body p-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">Governance Posture Score</div>
                <div className="flex items-baseline gap-3">
                  <span className={`text-3xl font-bold ${bandColor}`} data-testid="posture-score-value">{posture.overall_score}%</span>
                  <span className={`text-sm font-medium ${bandColor}`} data-testid="posture-score-band">{posture.overall_band}</span>
                </div>
              </div>
              <div className="flex gap-6 text-xs">
                <div className="text-center">
                  <div className="text-white font-bold text-lg" data-testid="controls-total-count">{posture.total_controls}</div>
                  <div className="text-[#8B95A5]">{t('governance:controls.totalControls')}</div>
                </div>
                <div className="text-center">
                  <div className="text-emerald-400 font-bold text-lg">{posture.met_count}</div>
                  <div className="text-[#8B95A5]">{t('governance:controls.effective')}</div>
                </div>
                <div className="text-center">
                  <div className="text-yellow-400 font-bold text-lg">{posture.partial_count}</div>
                  <div className="text-[#8B95A5]">{t('governance:controls.needsReview')}</div>
                </div>
                <div className="text-center">
                  <div className="text-red-400 font-bold text-lg">{posture.unmet_count}</div>
                  <div className="text-[#8B95A5]">{t('governance:controls.gaps')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Domain Score Cards */}
      {posture?.domain_scores && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="domain-score-grid">
          {Object.entries(posture.domain_scores).map(([domain, ds]) => {
            const cfg = DOMAIN_CONFIG[domain] || DOMAIN_CONFIG.governance;
            const Icon = cfg.icon;
            const dBandColor = ds.band === 'Strong' ? 'text-emerald-400'
              : ds.band === 'Controlled with gaps' ? 'text-blue-400'
              : ds.band === 'Weak / Improvement required' ? 'text-yellow-400'
              : 'text-red-400';
            return (
              <div key={domain} className={`qms-card ${cfg.border}`} data-testid={`domain-score-${domain}`}>
                <div className="qms-card-body p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={cfg.color} size={16} />
                    <span className="text-white text-xs font-medium truncate">{ds.label}</span>
                  </div>
                  <div className={`text-xl font-bold ${dBandColor}`}>{ds.score}%</div>
                  <div className="text-[#8B95A5] text-xs">{ds.control_count} controls</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Governance Posture Notice */}
      <div className="qms-card border-[rgba(54,169,225,0.2)] mb-6">
        <div className="qms-card-body p-4">
          <div className="flex items-start gap-3">
            <ShieldAlert className="text-[#36A9E1] shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-[#36A9E1] font-medium text-sm mb-1">Defense-in-Depth Governance Posture</h4>
              <p className="text-[#8B95A5] text-xs leading-relaxed">
                This control library supports governance maturity assessment and remediation readiness. Scores reflect the current assessment state and are deterministic based on control evaluation status. This does not represent a claim of breach-proof security.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="qms-card mb-6">
        <div className="qms-card-body p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A5]" size={16} />
              <input
                type="text"
                placeholder="Search controls..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm placeholder-[#8B95A5] focus:border-[#36A9E1] focus:outline-none"
                data-testid="controls-search-input"
              />
            </div>
            <select
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              className="px-3 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm focus:border-[#36A9E1] focus:outline-none"
              data-testid="controls-domain-filter"
            >
              <option value="all">All Domains</option>
              {Object.entries(DOMAIN_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="px-3 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm focus:border-[#36A9E1] focus:outline-none"
              data-testid="controls-severity-filter"
            >
              <option value="all">All Severity</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="qms-card border-red-500/30 mb-6">
          <div className="qms-card-body p-4 text-red-400 text-sm flex items-center gap-2">
            <AlertTriangle size={16} />
            {error}
          </div>
        </div>
      )}

      {/* Controls List */}
      <div className="space-y-3" data-testid="controls-list">
        {filteredControls.map(c => <ControlCard key={c.control_id} control={c} />)}
      </div>

      {filteredControls.length === 0 && !loading && !error && (
        <div className="qms-card">
          <div className="qms-card-body p-8 text-center">
            <FileText className="mx-auto mb-4 text-[#8B95A5]" size={48} />
            <h3 className="text-white font-semibold mb-2">No Controls Found</h3>
            <p className="text-[#8B95A5] text-sm">Try adjusting your filters</p>
          </div>
        </div>
      )}

      {/* Controls Count Footer */}
      <div className="mt-4 text-center text-[#8B95A5] text-xs">
        Showing {filteredControls.length} of {controls.length} controls
      </div>
    </QMSLayout>
  );
};

export default GovernanceControlsPage;
